import { useDictionaryContext } from "@/components/dictionaries/DictionaryContext";
import DictionaryForm from "@/components/dictionaries/DictionaryForm";
import ServicesAndGoodsSelect from "@/components/ServicesAndGoodsSelect";
import Field from "@/components/Field";
import Fieldset from "@/components/Fieldset";
import PageContent from "@/components/PageContent";
import ProjectDependsOnSelect from "@/components/ProjectDependsOnSelect";
import SaveButton from "@/components/SaveButton";
import CheckboxField from "@/components/CheckboxField";
import { Input } from "@/components/ui/input";
import { isRequired } from "@/lib/form";
import { useProjectTypesDictionary } from "@/modules/projectTypes/hooks/useProjectTypesDictionary";
import { useTranslation } from "react-i18next";
import { ReadProjectType, UpdateProjectType } from "@/types/projectType";

interface ProjectTypeFormProps {
  includeProjectDependsOn?: boolean;
}

export default function ProjectTypeForm({
  includeProjectDependsOn = true,
}: ProjectTypeFormProps) {
  const { t } = useTranslation();
  const config = useProjectTypesDictionary();
  const { Icon, ns } = config;
  const labels = config.getLabels(t);
  const { mutation, query } = useDictionaryContext<
    ReadProjectType,
    UpdateProjectType
  >();

  return (
    <DictionaryForm>
      <PageContent
        icon={<Icon className="w-6 h-6 mr-3" />}
        title={labels.title}
        footer={<SaveButton />}
      >
        <Fieldset className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field
            name="name"
            label={t(`${ns}.fields.name`)}
            required={isRequired(config.schema.shape.name)}
            render={({ field }) => (
              <Input
                disabled={mutation.isPending}
                className="w-full"
                {...field}
              />
            )}
          />

          <Field
            name="description"
            label={t(`${ns}.fields.description`)}
            required={isRequired(config.schema.shape.description)}
            render={({ field }) => (
              <Input
                disabled={mutation.isPending}
                className="w-full"
                {...field}
              />
            )}
          />

          <Field
            name="services_and_goods_id"
            label={t(`${ns}.fields.services_and_goods_code`)}
            required={isRequired(config.schema.shape.services_and_goods_id)}
            render={({ field }) => (
              <ServicesAndGoodsSelect
                disabled={mutation.isPending}
                className="w-full"
                defaultItem={{
                  id: query?.data?.services_and_goods_id,
                  name: query?.data?.services_and_goods,
                  code: query?.data?.services_and_goods_code,
                }}
                {...field}
              />
            )}
          />

          {includeProjectDependsOn && (
            <Field
              name="project_depends_on"
              label={t(`${ns}.fields.project_depends_on`)}
              required={isRequired(config.schema.shape.project_depends_on)}
              render={({ field }) => <ProjectDependsOnSelect {...field} />}
            />
          )}

          <CheckboxField
            name="available_for_structure"
            label={t(`${ns}.fields.available_for_structure`)}
            required={isRequired(config.schema.shape.available_for_structure)}
            disabled={mutation.isPending}
          />
        </Fieldset>
      </PageContent>
    </DictionaryForm>
  );
}
